import { useState, useEffect, useRef } from "react";

/* ─── Types ──────────────────────────────────────────────────── */
export type ContractTemplateRow = {
  id:         string;
  tenant_id:  string;
  name:       string;
  language:   string;
  content:    string;
  is_active:  boolean;
  created_at: string;
  updated_at: string;
};

export type TemplateForm = {
  name:      string;
  language:  string;
  content:   string;
  is_active: boolean;
};

interface Props {
  open:    boolean;
  editing: ContractTemplateRow | null;
  saving:  boolean;
  onClose: () => void;
  onSave:  (form: TemplateForm) => void;
}

/* ─── Languages ──────────────────────────────────────────────── */
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "pt", label: "Português" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
];

/* ─── Variables ──────────────────────────────────────────────── */
const VARIABLES = [
  { var: "{{client_name}}",    label: "Client Name",      icon: "ri-user-line" },
  { var: "{{business_name}}",  label: "Studio/Business",  icon: "ri-building-line" },
  { var: "{{contract_total}}", label: "Contract Total",   icon: "ri-money-dollar-circle-line" },
  { var: "{{deposit}}",        label: "Deposit Amount",   icon: "ri-coin-line" },
  { var: "{{balance}}",        label: "Balance",          icon: "ri-wallet-3-line" },
  { var: "{{event_date}}",     label: "Event Date",       icon: "ri-calendar-line" },
  { var: "{{event_type}}",     label: "Event Type",       icon: "ri-camera-line" },
  { var: "{{event_location}}",  label: "Event Location",  icon: "ri-map-pin-line" },
  { var: "{{today_date}}",     label: "Today's Date",     icon: "ri-calendar-check-line" },
  { var: "{{photographer_name}}", label: "Photographer",  icon: "ri-user-star-line" },
  { var: "{{delivery_weeks}}", label: "Delivery Weeks",   icon: "ri-time-line" },
  { var: "{{package_name}}",   label: "Package Name",     icon: "ri-gift-line" },
];

/* ─── Starter template ───────────────────────────────────────── */
const STARTER_CONTENT = `PHOTOGRAPHY SERVICES AGREEMENT

This Photography Services Agreement is entered into as of {{today_date}}, between {{business_name}} ("Photographer") and {{client_name}} ("Client").

1. SERVICES
Photographer agrees to provide photography services for {{event_type}} scheduled on {{event_date}} at {{event_location}}.

2. PAYMENT
The total fee is {{contract_total}}. A non-refundable deposit of {{deposit}} is due upon signing. The remaining balance of {{balance}} is due 14 days before the event.

3. DELIVERY
Edited digital photographs will be delivered within {{delivery_weeks}} weeks after the event via a secure online gallery.

4. COPYRIGHT & USAGE
Photographer retains full copyright of all images. Client receives a non-exclusive personal license for non-commercial use. Photographer may use images for portfolio and marketing.

5. CANCELLATION
Cancellations with less than 30 days notice forfeit the deposit. Photographer will make reasonable efforts to reschedule in emergencies.

6. LIMITATION OF LIABILITY
In the event of circumstances beyond Photographer's control, liability is limited to a full refund of all payments received.

7. ENTIRE AGREEMENT
This Agreement constitutes the entire agreement between the parties.

---

CLIENT SIGNATURE: _________________________ DATE: __________

PHOTOGRAPHER SIGNATURE: __________________ DATE: __________

{{business_name}} · {{photographer_name}}`;

const EMPTY: TemplateForm = {
  name:      "",
  language:  "en",
  content:   "",
  is_active: true,
};

const INPUT = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors";

/* ─── Component ──────────────────────────────────────────────── */
export default function TemplateModal({ open, editing, saving, onClose, onSave }: Props) {
  const [form, setForm]         = useState<TemplateForm>(EMPTY);
  const [errors, setErrors]     = useState<{ name?: string; content?: string }>({});
  const textareaRef             = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ name: editing.name, language: editing.language, content: editing.content, is_active: editing.is_active });
    } else {
      setForm(EMPTY);
    }
    setErrors({});
  }, [open, editing]);

  const set = <K extends keyof TemplateForm>(key: K, val: TemplateForm[K]) =>
    setForm((p) => ({ ...p, [key]: val }));

  /* Insert placeholder at cursor */
  const insertVar = (placeholder: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const newContent = form.content.substring(0, start) + placeholder + form.content.substring(end);
    set("content", newContent);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!form.name.trim())    e.name    = "Name is required";
    if (!form.content.trim()) e.content = "Content is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  if (!open) return null;

  /* Count variables in content */
  const usedVars = [...new Set((form.content.match(/\{\{[^}]+\}\}/g) ?? []))];
  const charCount = form.content.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-5xl bg-white rounded-2xl border border-slate-200 shadow-xl flex flex-col" style={{ maxHeight: "92vh" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className={`text-[#C8A96E] text-sm ${editing ? "ri-edit-2-line" : "ri-file-add-line"}`}></i>
            </div>
            <div>
              <h2 className="text-[#0f172a] font-semibold text-sm">{editing ? "Edit Template" : "New Template"}</h2>
              <p className="text-slate-400 text-xs">{editing ? editing.name : "Create a reusable contract template"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                onClick={() => set("content", STARTER_CONTENT)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#C8A96E] hover:text-[#b8965a] border border-[#C8A96E]/30 hover:border-[#C8A96E]/50 bg-[#C8A96E]/5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-magic-line"></i>
                Use Starter Template
              </button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex gap-0 flex-1 min-h-0">

          {/* ── Editor ── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-y-auto px-6 py-5 space-y-4">
            {/* Name + Language */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">
                  Template Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Wedding Photography Contract 2025"
                  className={`${INPUT} ${errors.name ? "border-red-300" : ""}`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">Language</label>
                <select
                  value={form.language}
                  onChange={(e) => set("language", e.target.value)}
                  className={INPUT + " cursor-pointer"}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-slate-500 text-xs uppercase tracking-wider">
                  Contract Content <span className="text-red-400">*</span>
                </label>
                <span className={`text-[10px] font-mono ${charCount > 9000 ? "text-amber-500" : "text-slate-300"}`}>
                  {charCount.toLocaleString()} chars
                </span>
              </div>

              {/* Used variables preview */}
              {usedVars.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1 p-2.5 rounded-lg bg-[#C8A96E]/5 border border-[#C8A96E]/15">
                  <p className="w-full text-slate-400 text-[10px] mb-1">Variables in use:</p>
                  {usedVars.map((v) => (
                    <span
                      key={v}
                      className="px-1.5 py-0.5 rounded bg-[#C8A96E]/10 border border-[#C8A96E]/20 text-[#C8A96E] text-[10px] font-mono"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              )}

              <textarea
                ref={textareaRef}
                value={form.content}
                onChange={(e) => set("content", e.target.value)}
                rows={22}
                placeholder="Enter your contract text here. Click any variable on the right to insert it at the cursor position…"
                className={`w-full bg-slate-50 border rounded-lg px-4 py-3 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-y font-mono leading-relaxed ${
                  errors.content ? "border-red-300" : "border-slate-200"
                }`}
              />
              {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content}</p>}
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="text-[#0f172a] text-sm font-medium">Active Template</p>
                <p className="text-slate-400 text-xs mt-0.5">
                  {form.is_active ? "Available when creating contracts" : "Hidden from template selection"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => set("is_active", !form.is_active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.is_active ? "bg-[#C8A96E]" : "bg-slate-200"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${form.is_active ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </div>

          {/* ── Right panel: Variable reference ── */}
          <div className="w-52 flex-shrink-0 border-l border-slate-200 overflow-y-auto px-4 py-5 space-y-3 bg-slate-50/50">
            <div>
              <h4 className="text-slate-400 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <i className="ri-code-s-slash-line"></i>
                Variables
              </h4>
              <p className="text-slate-400 text-[10px] mb-3 leading-relaxed">
                Click to insert at cursor position:
              </p>
              <div className="space-y-1">
                {VARIABLES.map((v) => (
                  <button
                    key={v.var}
                    type="button"
                    onClick={() => insertVar(v.var)}
                    className="w-full text-left group px-2.5 py-2 rounded-lg hover:bg-[#C8A96E]/8 border border-transparent hover:border-[#C8A96E]/20 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-[#C8A96E] flex-shrink-0">
                        <i className={`${v.icon} text-xs`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-[#C8A96E]/60 text-[9px] font-mono group-hover:text-[#C8A96E] transition-colors truncate">{v.var}</p>
                        <p className="text-slate-400 text-[9px]">{v.label}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-slate-200">
              <p className="text-slate-300 text-[9px] leading-relaxed">
                Variables are replaced with real data when a contract is generated from this template.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 flex-shrink-0">
          <div className="text-slate-300 text-xs">
            {form.content.split("\n").length} lines · {usedVars.length} variables
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={() => { if (validate()) onSave(form); }}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i> Saving…</>
              ) : (
                <><i className="ri-save-line"></i> {editing ? "Save Changes" : "Create Template"}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}